<template>
  <div class="acomodador">
    <div class="columns is-variable is-3">
      <!-- Páginas del PDF con los datos encima, para arrastrarlos -->
      <div class="column is-8">
        <div v-if="error" class="notification is-danger is-light">{{ error }}</div>
        <div v-else-if="cargando" class="has-text-centered py-6 has-text-grey">
          <b-icon icon="spinner" pack="fas" custom-class="fa-spin"></b-icon> {{ t("Cargando el PDF…", "Loading PDF…") }}
        </div>
        <div v-show="!cargando && !error" ref="hojas" class="hojas">
          <div
            v-for="(pagina, i) in paginas"
            :key="i"
            class="hoja"
            :style="{ width: `${pagina.ancho}px`, height: `${pagina.alto}px` }"
            @pointerdown.self="seleccionado = null"
          >
            <canvas :ref="`lienzo${i}`" class="lienzo"></canvas>
            <span class="numero-pagina">{{ t("Página", "Page") }} {{ i + 1 }}</span>
            <template v-for="(c, n) in value">
              <div
                v-if="c.Pagina === i"
                :key="`c${n}`"
                class="campo"
                :class="{ activo: seleccionado === n }"
                :style="estiloCampo(c, pagina)"
                @pointerdown.prevent="empezarArrastre($event, n, i)"
              >
                <span v-if="c.Tapar" class="tapa" :style="estiloTapa(c, pagina)"></span>
                <span class="texto" :style="estiloTexto(c)">{{ ejemplo[c.Dato] || c.Dato }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Lista de datos y propiedades del seleccionado -->
      <div class="column is-4">
        <div class="panel-lateral">
          <p class="has-text-weight-bold mb-2">{{ t("Agregar un dato", "Add a field") }}</p>
          <b-field grouped>
            <b-select v-model="nuevo.Dato" expanded size="is-small">
              <option v-for="d in datos" :key="d.clave" :value="d.clave">{{ d[iso] || d.es }}</option>
            </b-select>
            <b-select v-model.number="nuevo.Pagina" size="is-small">
              <option v-for="(p, i) in paginas" :key="i" :value="i">{{ t("Pág.", "Pg.") }} {{ i + 1 }}</option>
            </b-select>
            <p class="control">
              <b-button size="is-small" type="is-success" icon-left="plus" icon-pack="fas" :disabled="!paginas.length" @click="agregar" />
            </p>
          </b-field>
          <p class="is-size-7 has-text-grey mb-3">
            {{ t("Aparece en el centro de la página: arrástralo a su lugar.", "It appears in the middle of the page: drag it into place.") }}
          </p>

          <div v-if="campoActual" class="box editor-campo">
            <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
              <b>{{ nombreDato(campoActual.Dato) }}</b>
              <b-button size="is-small" type="is-danger is-light" icon-left="trash" icon-pack="fas" @click="quitar(seleccionado)" />
            </div>
            <b-field :label="t('Dato', 'Field')" label-position="on-border" class="mb-3">
              <b-select :value="campoActual.Dato" expanded size="is-small" @input="cambiar('Dato', $event)">
                <option v-for="d in datos" :key="d.clave" :value="d.clave">{{ d[iso] || d.es }}</option>
              </b-select>
            </b-field>
            <div class="columns is-mobile is-multiline is-variable is-1">
              <div class="column is-6">
                <b-field :label="t('Tamaño', 'Size')" label-position="on-border">
                  <b-numberinput :value="campoActual.Tamano" size="is-small" :min="4" :max="72" controls-position="compact" @input="cambiar('Tamano', $event)" />
                </b-field>
              </div>
              <div class="column is-6">
                <b-field label="Color" label-position="on-border">
                  <input type="color" class="input is-small" :value="campoActual.Color" @input="cambiar('Color', $event.target.value)" />
                </b-field>
              </div>
              <div class="column is-6">
                <b-field :label="t('Alineación', 'Alignment')" label-position="on-border">
                  <b-select :value="campoActual.Alineacion" size="is-small" expanded @input="cambiar('Alineacion', $event)">
                    <option value="izquierda">{{ t("Izquierda", "Left") }}</option>
                    <option value="centro">{{ t("Centro", "Center") }}</option>
                    <option value="derecha">{{ t("Derecha", "Right") }}</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column is-6 is-flex is-align-items-center">
                <b-checkbox :value="campoActual.Negrita" size="is-small" @input="cambiar('Negrita', $event)">{{ t("Negrita", "Bold") }}</b-checkbox>
              </div>
              <div class="column is-6">
                <b-field label="X %" label-position="on-border">
                  <b-input :value="pct(campoActual.X)" size="is-small" type="number" step="0.1" @input="cambiar('X', desdePct($event))" />
                </b-field>
              </div>
              <div class="column is-6">
                <b-field label="Y %" label-position="on-border">
                  <b-input :value="pct(campoActual.Y)" size="is-small" type="number" step="0.1" @input="cambiar('Y', desdePct($event))" />
                </b-field>
              </div>
              <div class="column is-12">
                <b-field :label="t('Tapar la raya (ancho %)', 'Cover the line (width %)')" label-position="on-border">
                  <b-input :value="pct(campoActual.Tapar)" size="is-small" type="number" step="0.5" min="0" @input="cambiar('Tapar', desdePct($event))" />
                </b-field>
                <p class="is-size-7 has-text-grey">
                  {{ t("Pinta un recuadro blanco bajo el dato, para PDFs que traen una raya ____ donde va el texto.", "Paints a white box under the field, for PDFs with a ____ line where the text goes.") }}
                </p>
              </div>
            </div>
            <p class="is-size-7 has-text-grey">{{ t("Con las flechas del teclado lo mueves fino (Shift = más).", "Arrow keys nudge it (Shift = more).") }}</p>
          </div>

          <p class="has-text-weight-bold mt-4 mb-2">{{ t("Datos en este PDF", "Fields on this PDF") }} ({{ value.length }})</p>
          <div class="lista-campos">
            <a
              v-for="(c, n) in value"
              :key="n"
              class="item-campo"
              :class="{ activo: seleccionado === n }"
              @click="seleccionar(n)"
            >
              <span>{{ nombreDato(c.Dato) }}</span>
              <span class="has-text-grey is-size-7">{{ t("pág.", "pg.") }} {{ c.Pagina + 1 }}</span>
            </a>
            <p v-if="!value.length" class="has-text-grey is-size-7">{{ t("Aún no hay datos.", "No fields yet.") }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// pdf.js desde cdnjs (versión fija): solo esta pantalla lo usa, así no se agrega una dependencia al build
const PDFJS = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174";
let cargaPdfjs = null;
const pdfjs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!cargaPdfjs) {
    cargaPdfjs = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `${PDFJS}/pdf.min.js`;
      s.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS}/pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      };
      s.onerror = () => {
        cargaPdfjs = null;
        reject(new Error("No se pudo cargar el visor de PDF"));
      };
      document.head.appendChild(s);
    });
  }
  return cargaPdfjs;
};

const ANCHO_MAX = 760;
const limitar = (v) => Math.min(Math.max(v, 0), 1);

export default {
  name: "AcomodadorPdf",
  props: {
    value: { type: Array, required: true }, // campos de este idioma
    pdf: { type: ArrayBuffer, default: null },
    tamanos: { type: Array, default: () => [] }, // tamaño real de cada página en puntos { Ancho, Alto }
    idioma: { type: String, required: true },
    datos: { type: Array, required: true },
    ejemplo: { type: Object, default: () => ({}) },
    iso: { type: String, default: "es" },
  },
  data() {
    return {
      paginas: [], // { ancho, alto } en píxeles en pantalla
      cargando: false,
      error: null,
      seleccionado: null,
      arrastre: null,
      nuevo: { Dato: "codigo", Pagina: 0 },
    };
  },
  computed: {
    campoActual() {
      return this.seleccionado !== null ? this.value[this.seleccionado] : null;
    },
  },
  watch: {
    pdf: { immediate: true, handler: "pintar" },
  },
  mounted() {
    window.addEventListener("keydown", this.teclas);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.teclas);
    this.soltar();
  },
  methods: {
    t(es, en) {
      return this.iso === "en" ? en : es;
    },
    nombreDato(clave) {
      const d = this.datos.find((x) => x.clave === clave);
      return d ? d[this.iso] || d.es : clave;
    },
    pct(v) {
      return Math.round((v || 0) * 1000) / 10;
    },
    desdePct(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? limitar(n / 100) : 0;
    },
    async pintar() {
      this.paginas = [];
      this.error = null;
      if (!this.pdf) return;
      this.cargando = true;
      try {
        const lib = await pdfjs();
        const doc = await lib.getDocument({ data: new Uint8Array(this.pdf.slice(0)) }).promise;
        const ancho = Math.min(this.$refs.hojas?.clientWidth || ANCHO_MAX, ANCHO_MAX);
        const vistas = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const pagina = await doc.getPage(i);
          const base = pagina.getViewport({ scale: 1 });
          const vista = pagina.getViewport({ scale: ancho / base.width });
          vistas.push({ pagina, vista });
        }
        this.paginas = vistas.map(({ vista }) => ({ ancho: vista.width, alto: vista.height }));
        await this.$nextTick();
        const nitidez = window.devicePixelRatio || 1;
        for (const [i, { pagina, vista }] of vistas.entries()) {
          const lienzo = this.$refs[`lienzo${i}`]?.[0];
          if (!lienzo) continue;
          lienzo.width = vista.width * nitidez;
          lienzo.height = vista.height * nitidez;
          lienzo.style.width = `${vista.width}px`;
          lienzo.style.height = `${vista.height}px`;
          const ctx = lienzo.getContext("2d");
          ctx.setTransform(nitidez, 0, 0, nitidez, 0, 0);
          await pagina.render({ canvasContext: ctx, viewport: vista }).promise;
        }
      } catch (e) {
        console.error(e);
        this.error = this.t("No se pudo mostrar el PDF. Revisa tu conexión y vuelve a abrirlo.", "Couldn't display the PDF.");
      } finally {
        this.cargando = false;
      }
    },
    // La escala de la letra en pantalla: 1 punto del PDF = ancho en pantalla / ancho real
    escala(pagina) {
      const real = this.tamanos[this.paginas.indexOf(pagina)];
      return real && pagina ? pagina.ancho / real.Ancho : 1;
    },
    estiloCampo(c, pagina) {
      const mover = c.Alineacion === "centro" ? "-50%" : c.Alineacion === "derecha" ? "-100%" : "0";
      return { left: `${c.X * pagina.ancho}px`, top: `${c.Y * pagina.alto}px`, transform: `translate(${mover}, -80%)` };
    },
    estiloTexto(c) {
      const pagina = this.paginas[c.Pagina];
      return {
        fontSize: `${(c.Tamano || 12) * this.escala(pagina)}px`,
        color: c.Color || "#000",
        fontWeight: c.Negrita ? 700 : 400,
      };
    },
    estiloTapa(c, pagina) {
      return { width: `${c.Tapar * pagina.ancho}px` };
    },
    emitir(campos) {
      this.$emit("input", campos);
    },
    cambiar(prop, valor) {
      if (this.seleccionado === null) return;
      const campos = this.value.map((c, i) => (i === this.seleccionado ? { ...c, [prop]: valor } : c));
      this.emitir(campos);
    },
    agregar() {
      const campo = {
        Idioma: this.idioma,
        Pagina: this.nuevo.Pagina,
        Dato: this.nuevo.Dato,
        X: 0.4,
        Y: 0.5,
        Tamano: 12,
        Color: "#000000",
        Negrita: false,
        Alineacion: "izquierda",
        Tapar: 0,
      };
      this.emitir([...this.value, campo]);
      this.seleccionado = this.value.length;
      this.$nextTick(() => this.$refs.hojas?.children[campo.Pagina]?.scrollIntoView({ behavior: "smooth", block: "center" }));
    },
    quitar(n) {
      this.emitir(this.value.filter((c, i) => i !== n));
      this.seleccionado = null;
    },
    seleccionar(n) {
      this.seleccionado = n;
      const hoja = this.$refs.hojas?.children[this.value[n].Pagina];
      hoja?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    },
    empezarArrastre(evento, n, pagina) {
      this.seleccionado = n;
      const hoja = this.$refs.hojas.children[pagina];
      const campo = this.value[n];
      const rect = hoja.getBoundingClientRect();
      this.arrastre = { n, rect, dx: evento.clientX - (rect.left + campo.X * rect.width), dy: evento.clientY - (rect.top + campo.Y * rect.height) };
      window.addEventListener("pointermove", this.mover);
      window.addEventListener("pointerup", this.soltar);
    },
    mover(evento) {
      if (!this.arrastre) return;
      const { n, rect, dx, dy } = this.arrastre;
      const X = limitar((evento.clientX - dx - rect.left) / rect.width);
      const Y = limitar((evento.clientY - dy - rect.top) / rect.height);
      this.emitir(this.value.map((c, i) => (i === n ? { ...c, X: Math.round(X * 10000) / 10000, Y: Math.round(Y * 10000) / 10000 } : c)));
    },
    soltar() {
      this.arrastre = null;
      window.removeEventListener("pointermove", this.mover);
      window.removeEventListener("pointerup", this.soltar);
    },
    teclas(evento) {
      if (this.seleccionado === null || !this.campoActual) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const paso = evento.shiftKey ? 0.01 : 0.001;
      const mov = { ArrowLeft: ["X", -paso], ArrowRight: ["X", paso], ArrowUp: ["Y", -paso], ArrowDown: ["Y", paso] }[evento.key];
      if (!mov) return;
      evento.preventDefault();
      const [prop, delta] = mov;
      this.cambiar(prop, Math.round(limitar(this.campoActual[prop] + delta) * 10000) / 10000);
    },
  },
};
</script>

<style scoped>
.hojas {
  max-height: 70vh;
  overflow: auto;
  padding: 8px;
  background: #eef0f4;
  border-radius: 6px;
}
.hoja {
  position: relative;
  margin: 0 auto 16px;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.18);
  touch-action: none;
}
.lienzo {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.numero-pagina {
  position: absolute;
  right: 6px;
  bottom: -18px;
  font-size: 11px;
  color: #888;
}
.campo {
  position: absolute;
  cursor: move;
  white-space: nowrap;
  line-height: 1;
  outline: 1px dashed rgba(72, 95, 199, 0.7);
  outline-offset: 2px;
  user-select: none;
}
.campo.activo {
  outline: 2px solid #485fc7;
  background: rgba(72, 95, 199, 0.08);
}
.campo .tapa {
  position: absolute;
  left: -2px;
  top: -2px;
  bottom: -2px;
  background: #fff;
  outline: 1px dotted #f14668;
  z-index: -1;
}
.texto {
  font-family: Helvetica, Arial, sans-serif;
}
.panel-lateral {
  position: sticky;
  top: 0;
}
.lista-campos {
  max-height: 26vh;
  overflow: auto;
}
.item-campo {
  display: flex;
  justify-content: space-between;
  padding: 4px 8px;
  border-radius: 4px;
  color: inherit;
}
.item-campo.activo,
.item-campo:hover {
  background: #eef1fb;
}
.editor-campo {
  padding: 12px;
}
</style>
